import React, { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from 'src/lib/supabase';
import AppLogo from "../components/AppLogo"; // NOTE: adjust path if your components folder lives elsewhere


/* ─── Fonts ─── */
(function injectFonts() {
  if (typeof document === "undefined") return;
  if (document.querySelector('link[href*="Instrument+Serif"]')) return;
  const l = document.createElement("link");
  l.rel = "stylesheet";
  l.href =
    "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
})();

/* ─── Tokens ─── */
const T = {
  /* greens */
  g600: "#155c36", g500: "#1e9254", g400: "#34b56e",
  g200: "#a7d9bc", g100: "#d6f0e3", g50:  "#eef8f3",
  /* neutrals */
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549",
  n600: "#4a5268", n500: "#6b7388", n400: "#8a93a8",
  n300: "#b0b7c8", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50:  "#f7f8fa", white: "#ffffff",
  /* semantic */
  red: "#c0392b", redL: "#fdf3f2", redB: "#f5c0bb",
  amber: "#b45309", amberL: "#fffbeb",
  /* type */
  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

/* ─── Inline Lucide SVGs ─── */
const ICONS = {
  UserRound:       <><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></>,
  BriefcaseBusiness: <><path d="M12 12h.01"/><path d="M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><rect width="20" height="14" x="2" y="6" rx="2"/><path d="M12 12v.01"/></>,
  ShieldCheck:     <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
  Wallet:          <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/></>,
  CalendarDays:    <><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/></>,
  BadgeCheck:      <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
  Upload:          <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></>,
  Check:           <polyline points="20 6 9 14 4 10"/>,
  ChevronRight:    <path d="m9 18 6-6-6-6"/>,
  X:               <><path d="M18 6 6 18"/><path d="m6 6 12 12"/></>,
  ArrowRight:      <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
  Lock:            <><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
  Info:            <><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></>,
  Minus:           <path d="M5 12h14"/>,
  Plus:            <><path d="M5 12h14"/><path d="M12 5v14"/></>,
  MapPin:          <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></>,
  Phone:           <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.07 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>,
  Mail:            <><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
  Star:            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>,
  TrendingUp:      <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
};

const Ico = ({ name, size = 20, color = "currentColor", sw = 1.5 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size}
    viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: "block" }}>
    {ICONS[name]}
  </svg>
);

/* ─── Service categories (fallback if Supabase is empty) ───
   NOTE: this list only renders when the `categories` table query
   returns no rows. The real source of truth is Supabase — make
   sure the same 15 categories (including "Jirani Chef") exist
   there, or new pros signing up against a populated table will
   never see this fallback at all. */
const FALLBACK_CATEGORIES = [
  { id: "c1",  name: "Cleaning & Laundry",      icon: "Star" },
  { id: "c2",  name: "Plumbing",                icon: "Star" },
  { id: "c3",  name: "Electrical Services",     icon: "Star" },
  { id: "c4",  name: "TV & Electronics Repair", icon: "Star" },
  { id: "c5",  name: "Hair & Beauty",           icon: "Star" },
  { id: "c6",  name: "Home Tutoring",           icon: "Star" },
  { id: "c7",  name: "Photography",             icon: "Star" },
  { id: "c8",  name: "Gardening & Landscaping", icon: "Star" },
  { id: "c9",  name: "Carpentry & Furniture",   icon: "Star" },
  { id: "c10", name: "Moving & Relocation",     icon: "Star" },
  { id: "c11", name: "Babysitting & Childcare", icon: "Star" },
  { id: "c12", name: "Fitness & Training",      icon: "Star" },
  { id: "c13", name: "Auto Repair",             icon: "Star" },
  { id: "c14", name: "Errands & Delivery",      icon: "Star" },
  { id: "c15", name: "Jirani Chef",             icon: "Star" },
];

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

/* ─── Step config ─── */
const STEPS = [
  { id: "welcome",    icon: "BadgeCheck",       label: "Welcome"       },
  { id: "personal",   icon: "UserRound",        label: "About you"     },
  { id: "services",   icon: "BriefcaseBusiness",label: "Your services" },
  { id: "pricing",    icon: "Wallet",           label: "Set your rate" },
  { id: "identity",   icon: "ShieldCheck",      label: "Verify ID"     },
  { id: "availability",icon: "CalendarDays",    label: "Availability"  },
  { id: "review",     icon: "BadgeCheck",       label: "Review"        },
];

/* ─── Shared input style ─── */
const iStyle = (focused) => ({
  width: "100%", boxSizing: "border-box",
  padding: "13px 16px",
  border: `1px solid ${focused ? T.g500 : T.n200}`,
  borderRadius: 10, fontSize: 15, outline: "none",
  color: T.n900, fontFamily: T.sans, background: T.white,
  boxShadow: focused ? `0 0 0 3px ${T.g100}` : "none",
  transition: "all .15s",
});

const LBL = ({ children, required }) => (
  <label style={{ fontSize: 11, fontWeight: 600, color: T.n500, display: "block",
    marginBottom: 7, letterSpacing: "0.06em", textTransform: "uppercase" }}>
    {children}{required && <span style={{ color: T.red, fontWeight: 400,
      textTransform: "none", letterSpacing: 0 }}> required</span>}
  </label>
);

const ERR = ({ msg }) => msg ? (
  <p style={{ fontSize: 12, color: T.red, margin: "5px 0 0", fontFamily: T.sans }}>{msg}</p>
) : null;

/* ─── Spinner ─── */
const Spin = ({ size = 20, color = T.white }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="2.5"
    style={{ animation: "jrn-spin .75s linear infinite", display: "block" }}>
    <circle cx="12" cy="12" r="10" strokeOpacity=".25"/>
    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
  </svg>
);

/* ─── File upload zone ─── */
function UploadZone({ label, sublabel, file, onFile, hint }) {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  return (
    <div>
      <LBL>{label}</LBL>
      <div
        onClick={() => ref.current.click()}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
        style={{
          border: `1.5px dashed ${drag ? T.g500 : file ? T.g400 : T.n200}`,
          borderRadius: 12, padding: "20px 16px", textAlign: "center",
          cursor: "pointer", background: file ? T.g50 : drag ? T.g50 : T.n50,
          transition: "all .18s",
        }}
      >
        {file ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Ico name="Check" size={18} color={T.g500} sw={2}/>
            <span style={{ fontSize: 13, color: T.g600, fontWeight: 500, fontFamily: T.sans }}>{file.name}</span>
          </div>
        ) : (
          <>
            <Ico name="Upload" size={22} color={T.n400} sw={1.5}/>
            <p style={{ fontSize: 13, fontWeight: 500, color: T.n600, margin: "8px 0 2px", fontFamily: T.sans }}>{sublabel}</p>
            <p style={{ fontSize: 11, color: T.n400, margin: 0, fontFamily: T.sans }}>{hint || "JPG, PNG or PDF · max 10 MB"}</p>
          </>
        )}
        <input ref={ref} type="file" accept="image/*,.pdf"
          style={{ display: "none" }} onChange={e => e.target.files[0] && onFile(e.target.files[0])}/>
      </div>
    </div>
  );
}

/* ─── Nav buttons (Back / Continue) ─── */
function NavRow({ onBack, onNext, nextLabel = "Continue", canNext = true, loading = false, step, totalSteps, blockedHint }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 32, paddingTop: 24,
      borderTop: `1px solid ${T.n100}` }}>
      <button onClick={onNext} disabled={!canNext || loading} style={{
        width: "100%", padding: "15px", border: "none", borderRadius: 12,
        background: canNext ? T.g500 : T.n150,
        color: canNext ? T.white : T.n400,
        fontSize: 15, fontWeight: 600, cursor: canNext ? "pointer" : "not-allowed",
        fontFamily: T.sans, display: "flex", alignItems: "center",
        justifyContent: "center", gap: 8, transition: "all .18s",
        letterSpacing: "0.01em",
      }}
        onMouseEnter={e => { if (canNext) e.currentTarget.style.opacity = "0.88"; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
      >
        {loading ? <Spin size={18}/> : <>{nextLabel} {!loading && <Ico name="ArrowRight" size={16} color={T.white} sw={2}/>}</>}
      </button>
      {!canNext && blockedHint && (
        <p style={{ fontSize: 12, color: T.red, textAlign: "center", margin: 0, fontFamily: T.sans }}>
          {blockedHint}
        </p>
      )}
      {onBack && (
        <button onClick={onBack} style={{
          width: "100%", padding: "13px", border: `1px solid ${T.n200}`,
          borderRadius: 12, background: T.white, color: T.n600,
          fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: T.sans,
        }}>← Back</button>
      )}
      <p style={{ fontSize: 12, color: T.n400, textAlign: "center", margin: 0, fontFamily: T.sans }}>
        Step {step} of {totalSteps} · Your progress is saved automatically
      </p>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   STEP COMPONENTS
══════════════════════════════════════════════════ */

/* Step 0 — Welcome */
function StepWelcome({ onNext }) {
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  const handleStart = () => {
    setLoading(true);
    setTimeout(() => { setLoading(false); onNext(); }, 700);
  };

  /* Hero image — put your image at src/assets/onboarding-hero.jpg
     NOTE: only loaded/rendered on desktop now — see isMobile check below. */
  let heroImg = null;
  if (!isMobile) {
    try { heroImg = require("../assets/onboarding-hero.jpg"); } catch (_) {}
  }

  return (
    <div style={{ minHeight: isMobile ? "auto" : "calc(100vh - 64px)", display: "flex",
      flexDirection: isMobile ? "column" : "row", overflow: "hidden" }}>

      {/* Left — copy */}
      <div style={{ flex: isMobile ? "none" : "0 0 44%", padding: isMobile ? "40px 20px 32px" : "0 0 0 56px",
        display: "flex", flexDirection: "column", justifyContent: "center",
        maxWidth: isMobile ? "none" : 540 }}>

        <div style={{ display: "inline-flex", alignItems: "center", gap: 7,
          color: T.g600, fontSize: 11, fontWeight: 600, letterSpacing: "0.09em",
          textTransform: "uppercase", marginBottom: 24 }}>
          <Ico name="BadgeCheck" size={13} color={T.g500} sw={2}/>
          Become a JiraniPro
        </div>

        <h1 style={{ fontFamily: T.serif, fontSize: isMobile ? "clamp(36px,9vw,48px)" : "clamp(44px,4.2vw,60px)",
          fontWeight: 400, margin: "0 0 20px", lineHeight: 1.08,
          color: T.n900, letterSpacing: "-0.03em" }}>
          Apply once.<br/>
          <em style={{ fontStyle: "italic", color: T.g600 }}>Get verified.</em>
        </h1>

        <p style={{ fontSize: isMobile ? 15 : 17, color: T.n600, margin: "0 0 12px",
          lineHeight: 1.65, maxWidth: 420, fontFamily: T.sans }}>
          Only qualified professionals are accepted. Your profile and proof of work determine approval.
        </p>

        <p style={{ fontSize: 13, color: T.n400, margin: "0 0 32px", fontFamily: T.sans }}>
          Free to apply · No commitment · Takes about 10 minutes
        </p>

        {/* Trust bullets */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 28px", marginBottom: 36 }}>
          {["Verified identity","Secure application","14-day review","Save anytime"].map(l => (
            <div key={l} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Ico name="Check" size={13} color={T.g500} sw={2.5}/>
              <span style={{ fontSize: 13, color: T.n600, fontFamily: T.sans, fontWeight: 500 }}>{l}</span>
            </div>
          ))}
        </div>

        <button onClick={handleStart} disabled={loading} style={{
          height: isMobile ? 52 : 56, padding: "0 32px",
          borderRadius: 14, border: "none", background: T.g500,
          color: T.white, fontSize: 16, fontWeight: 600,
          fontFamily: T.sans, cursor: loading ? "default" : "pointer",
          display: "inline-flex", alignItems: "center", gap: 10,
          boxShadow: `0 4px 20px ${T.g500}44`,
          transition: "all .18s", alignSelf: "flex-start",
          letterSpacing: "0.01em",
        }}
          onMouseEnter={e => { if (!loading) { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "translateY(-2px)"; }}}
          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "none"; }}
        >
          {loading ? <Spin size={20}/> : <>Start your application <Ico name="ArrowRight" size={18} color={T.white} sw={2}/></>}
        </button>
      </div>

      {/* Right — image panel. Desktop only: never rendered on mobile.
          Runs full-bleed to the browser edge and fades softly into the
          copy panel on the left instead of a hard vertical seam. */}
      {!isMobile && (
        <div style={{ flex: 1, minHeight: "100%",
          position: "relative", overflow: "hidden", margin: "20px 0" }}>

          <div style={{
            position: "absolute", inset: 0,
            WebkitMaskImage: "linear-gradient(to right, transparent 0px, black 160px)",
            maskImage: "linear-gradient(to right, transparent 0px, black 160px)",
          }}>
            {heroImg ? (
              <img src={heroImg} alt="JiraniPro at work"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}/>
            ) : (
              <div style={{ width: "100%", height: "100%",
                background: `linear-gradient(155deg, ${T.g100} 0%, ${T.g400} 48%, ${T.g600} 100%)`,
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="180" height="240" viewBox="0 0 160 220" fill="none" style={{ opacity: .18 }}>
                  <ellipse cx="80" cy="64" rx="38" ry="40" fill="#064e3b"/>
                  <path d="M16 210 Q20 140 80 128 Q140 140 144 210Z" fill="#064e3b"/>
                </svg>
              </div>
            )}
            {/* Depth gradient so the floating card reads clearly over any photo */}
            <div style={{ position: "absolute", inset: 0,
              background: "linear-gradient(195deg, rgba(15,17,23,0) 42%, rgba(15,17,23,.5) 100%)" }}/>
          </div>

          {/* Floating stat card — glass, marketable, two-stat */}
          <div style={{ position: "absolute", bottom: 32, left: 40, right: 40, maxWidth: 400,
            background: "rgba(255,255,255,.14)",
            backdropFilter: "blur(20px) saturate(160%)",
            WebkitBackdropFilter: "blur(20px) saturate(160%)",
            border: "1px solid rgba(255,255,255,.28)",
            borderRadius: 20, padding: "20px 22px",
            boxShadow: "0 20px 48px rgba(6,20,14,.35)" }}>

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(255,255,255,.18)",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Ico name="TrendingUp" size={14} color={T.white} sw={2}/>
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,.85)",
                textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.sans }}>
                Trusted across Kenya
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "stretch", gap: 20 }}>
              <div>
                <p style={{ fontSize: 28, fontWeight: 700, color: T.white, margin: 0,
                  fontFamily: T.sans, letterSpacing: "-0.02em", lineHeight: 1 }}>2,400+</p>
                <p style={{ fontSize: 12.5, color: "rgba(255,255,255,.78)", margin: "5px 0 0",
                  fontFamily: T.sans }}>Verified pros</p>
              </div>
              <div style={{ width: 1, background: "rgba(255,255,255,.25)" }}/>
              <div>
                <p style={{ fontSize: 28, fontWeight: 700, color: T.white, margin: 0,
                  fontFamily: T.sans, letterSpacing: "-0.02em", lineHeight: 1 }}>45K</p>
                <p style={{ fontSize: 12.5, color: "rgba(255,255,255,.78)", margin: "5px 0 0",
                  fontFamily: T.sans }}>Avg. KES earned / mo</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Step 1 — Personal Info (email field removed per product request;
   email is still captured silently from supabase.auth in the parent
   component and submitted with the profile, it's just not shown here) */
function StepPersonal({ data, onChange, onNext, onBack }) {
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState(null);
  const isMobile = useIsMobile();

  const validate = () => {
    const e = {};
    const phoneRx = /^(07|01|\+254)\d{8}$/;
    if (!data.fullName.trim()) e.fullName = "Full name is required";
    if (!data.phone.trim()) e.phone = "Phone number is required";
    else if (!phoneRx.test(data.phone)) e.phone = "Enter a valid Kenyan number (07XX, 01XX, or +254…)";
    if (!data.location.trim()) e.location = "Location is required";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const fields = [
    { key: "fullName", label: "Full Name", placeholder: "e.g. Mary Wanjiku", type: "text", required: true, icon: "UserRound" },
    { key: "phone", label: "Phone Number", placeholder: "07XX XXX XXX", type: "tel", required: true, icon: "Phone" },
    { key: "location", label: "Your Area", placeholder: "e.g. Westlands, Nairobi", type: "text", required: true, icon: "MapPin" },
  ];

  return (
    <StepShell title="Tell us about yourself"
      subtitle="This is how clients and Jirani will reach you.">
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {fields.map(f => (
          <div key={f.key}>
            <LBL required={f.required}>{f.label}</LBL>
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", left: 14, top: "50%",
                transform: "translateY(-50%)", pointerEvents: "none" }}>
                <Ico name={f.icon} size={16} color={focused === f.key ? T.g500 : T.n400} sw={1.5}/>
              </div>
              <input type={f.type} value={data[f.key]}
                onChange={e => onChange({ [f.key]: e.target.value })}
                onFocus={() => setFocused(f.key)} onBlur={() => setFocused(null)}
                placeholder={f.placeholder}
                style={{ ...iStyle(focused === f.key), paddingLeft: 42 }}/>
            </div>
            <ERR msg={errors[f.key]}/>
          </div>
        ))}
      </div>
      <NavRow onBack={onBack} onNext={() => { if (validate()) onNext(); }}
        nextLabel="Continue" canNext={true} step={2} totalSteps={6}/>
    </StepShell>
  );
}

/* Step 2 — Services & Skills */
function StepServices({ categories, onNext, onBack, selected, onToggle }) {
  const isMobile = useIsMobile();
  const cats = categories.length ? categories : FALLBACK_CATEGORIES;

  return (
    <StepShell title="What services do you offer?"
      subtitle="Select everything that applies. You can add more later.">
      <div style={{ display: "grid",
        gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr", gap: 10 }}>
        {cats.map(cat => {
          const sel = selected.has(cat.id);
          return (
            <div key={cat.id} onClick={() => onToggle(cat.id)}
              style={{
                border: `1.5px solid ${sel ? T.g500 : T.n150}`,
                borderRadius: 12, padding: "14px 12px",
                cursor: "pointer", background: sel ? T.g50 : T.white,
                transition: "all .18s",
                display: "flex", flexDirection: "column", gap: 8,
                position: "relative",
              }}
              onMouseEnter={e => { if (!sel) e.currentTarget.style.borderColor = T.n300; }}
              onMouseLeave={e => { if (!sel) e.currentTarget.style.borderColor = T.n150; }}
            >
              {sel && (
                <div style={{ position: "absolute", top: 8, right: 8,
                  width: 18, height: 18, borderRadius: "50%", background: T.g500,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Ico name="Check" size={10} color={T.white} sw={3}/>
                </div>
              )}
              <p style={{ fontSize: 13, fontWeight: sel ? 600 : 400,
                color: sel ? T.g700 : T.n700, margin: 0,
                fontFamily: T.sans, lineHeight: 1.35 }}>{cat.name}</p>
            </div>
          );
        })}
      </div>
      {selected.size > 0 && (
        <div style={{ marginTop: 16, padding: "12px 16px", background: T.g50,
          borderRadius: 10, border: `1px solid ${T.g100}` }}>
          <p style={{ fontSize: 12, color: T.g600, margin: 0, fontFamily: T.sans, fontWeight: 500 }}>
            {selected.size} service{selected.size > 1 ? "s" : ""} selected
          </p>
        </div>
      )}
      <NavRow onBack={onBack} onNext={onNext} canNext={selected.size > 0}
        nextLabel="Continue" step={3} totalSteps={6}/>
    </StepShell>
  );
}

/* Step 3 — Pricing */
function StepPricing({ rate, onChange, onNext, onBack }) {
  const [focused, setFocused] = useState(false);
  const isMobile = useIsMobile();

  const presets = [500, 1000, 1500, 2000, 3000];

  return (
    <StepShell title="Set your base rate"
      subtitle="This is what clients see when they first find you. You can always negotiate per job.">

      {/* Rate input */}
      <div>
        <LBL required>Your hourly / base rate (KES)</LBL>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <span style={{ position: "absolute", left: 14, top: "50%",
              transform: "translateY(-50%)", fontSize: 14, fontWeight: 600,
              color: T.n500, fontFamily: T.sans, pointerEvents: "none" }}>KES</span>
            <input type="number" min={0} value={rate}
              onChange={e => onChange(parseInt(e.target.value) || 0)}
              onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
              style={{ ...iStyle(focused), paddingLeft: 52, fontSize: 22,
                fontWeight: 600, color: T.n900 }}/>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => onChange(Math.max(0, rate - 100))} style={{
              width: 40, height: 40, border: `1px solid ${T.n200}`, borderRadius: 10,
              background: T.white, cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center" }}>
              <Ico name="Minus" size={16} color={T.n600} sw={2}/>
            </button>
            <button onClick={() => onChange(rate + 100)} style={{
              width: 40, height: 40, border: `1px solid ${T.n200}`, borderRadius: 10,
              background: T.white, cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center" }}>
              <Ico name="Plus" size={16} color={T.n600} sw={2}/>
            </button>
          </div>
        </div>
        {/* Quick-select presets */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          {presets.map(p => (
            <button key={p} onClick={() => onChange(p)} style={{
              padding: "6px 14px", borderRadius: 100,
              border: `1px solid ${rate === p ? T.g500 : T.n200}`,
              background: rate === p ? T.g500 : T.white,
              color: rate === p ? T.white : T.n600,
              fontSize: 13, fontWeight: rate === p ? 600 : 400,
              cursor: "pointer", fontFamily: T.sans, transition: "all .15s",
            }}>{p.toLocaleString()}</button>
          ))}
        </div>
      </div>

      {/* Info card */}
      <div style={{ marginTop: 24, background: T.n50, borderRadius: 12,
        border: `1px solid ${T.n150}`, padding: "16px 18px",
        display: "flex", gap: 12, alignItems: "flex-start" }}>
        <Ico name="Info" size={16} color={T.n500} sw={1.5}/>
        <div>
          <p style={{ fontSize: 13, fontWeight: 600, color: T.n700, margin: "0 0 4px", fontFamily: T.sans }}>
            Jirani's recommended range for your area
          </p>
          <p style={{ fontSize: 13, color: T.n500, margin: 0, fontFamily: T.sans, lineHeight: 1.55 }}>
            Most pros in Nairobi charge between <strong style={{ color: T.n700 }}>KES 800 – 2,500</strong> per job.
            Setting a competitive rate helps you get your first bookings faster.
          </p>
        </div>
      </div>

      <NavRow onBack={onBack} onNext={onNext} canNext={rate > 0}
        nextLabel="Continue" step={4} totalSteps={6}/>
    </StepShell>
  );
}

/* Step 4 — Identity Verification
   Gate: user CANNOT proceed to step 5 until all three documents
   (ID front, ID back, selfie) are uploaded. Previously canNext was
   hardcoded true, letting people skip identity verification entirely. */
function StepIdentity({ data, onUpdate, onNext, onBack }) {
  const allUploaded = Boolean(data.idFront && data.idBack && data.selfie);

  return (
    <StepShell title="Verify your identity"
      subtitle="This keeps the platform safe and builds trust with clients. Your documents are encrypted — clients never see them.">

      {/* Trust notice */}
      <div style={{ background: T.g50, border: `1px solid ${T.g100}`,
        borderRadius: 12, padding: "14px 16px", display: "flex", gap: 10,
        alignItems: "flex-start", marginBottom: 28 }}>
        <Ico name="Lock" size={16} color={T.g600} sw={1.5}/>
        <p style={{ fontSize: 13, color: T.g700, margin: 0, lineHeight: 1.6, fontFamily: T.sans }}>
          Your documents are encrypted and only reviewed by Jirani's verification team.
          We never share them with clients.
        </p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <UploadZone label="Government ID — Front"
          sublabel="Upload the front of your National ID or Passport"
          file={data.idFront} onFile={f => onUpdate({ idFront: f })}/>

        <UploadZone label="Government ID — Back"
          sublabel="Upload the back of your National ID"
          file={data.idBack} onFile={f => onUpdate({ idBack: f })}/>

        <UploadZone label="Selfie / Face Photo"
          sublabel="A clear photo of your face — no sunglasses"
          file={data.selfie} onFile={f => onUpdate({ selfie: f })}/>
      </div>

      {/* Progress */}
      {(data.idFront || data.idBack || data.selfie) && (
        <div style={{ marginTop: 20, padding: "12px 16px", background: T.g50,
          border: `1px solid ${T.g100}`, borderRadius: 10 }}>
          <p style={{ fontSize: 12, color: T.g600, fontWeight: 600, margin: "0 0 8px", fontFamily: T.sans,
            textTransform: "uppercase", letterSpacing: "0.05em" }}>Uploaded</p>
          {[["Government ID (Front)", data.idFront], ["Government ID (Back)", data.idBack], ["Selfie", data.selfie]].map(([lbl, f]) => (
            <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <Ico name={f ? "Check" : "X"} size={13} color={f ? T.g500 : T.n300} sw={2.5}/>
              <span style={{ fontSize: 13, color: f ? T.n700 : T.n400, fontFamily: T.sans }}>{lbl}</span>
            </div>
          ))}
        </div>
      )}

      <NavRow onBack={onBack} onNext={onNext}
        nextLabel="Continue" canNext={allUploaded}
        blockedHint={!allUploaded ? "Upload all three documents to continue." : null}
        step={5} totalSteps={6}/>
    </StepShell>
  );
}

/* Step 5 — Availability */
function StepAvailability({ data, onChange, onNext, onBack }) {
  const HOURS = Array.from({ length: 14 }, (_, i) => {
    const h = i + 7;
    return `${h.toString().padStart(2,"0")}:00`;
  });

  const toggleDay = d => {
    const days = data.availableDays.includes(d)
      ? data.availableDays.filter(x => x !== d)
      : [...data.availableDays, d];
    onChange({ availableDays: days });
  };

  return (
    <StepShell title="When are you available?"
      subtitle="Clients will see your availability when booking. You can always update this later.">

      <div>
        <LBL>Working days</LBL>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {DAYS.map(d => {
            const sel = data.availableDays.includes(d);
            return (
              <button key={d} onClick={() => toggleDay(d)} style={{
                padding: "9px 16px", borderRadius: 100,
                border: `1.5px solid ${sel ? T.g500 : T.n200}`,
                background: sel ? T.g500 : T.white,
                color: sel ? T.white : T.n600,
                fontSize: 14, fontWeight: sel ? 600 : 400,
                cursor: "pointer", fontFamily: T.sans, transition: "all .15s",
              }}>{d}</button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 24 }}>
        {[
          { label: "Start time", key: "startHour" },
          { label: "End time", key: "endHour" },
        ].map(({ label, key }) => (
          <div key={key}>
            <LBL>{label}</LBL>
            <select value={data[key]} onChange={e => onChange({ [key]: e.target.value })}
              style={{ ...iStyle(false), appearance: "none",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238a93a8' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}>
              {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
        ))}
      </div>

      <NavRow onBack={onBack} onNext={onNext}
        canNext={data.availableDays.length > 0}
        nextLabel="Review application" step={6} totalSteps={6}/>
    </StepShell>
  );
}

/* Step 6 — Review & Submit */
function StepReview({ personal, selectedCategories, categories, rate, availability, onBack, onSubmit, loading }) {
  const cats = (categories.length ? categories : FALLBACK_CATEGORIES)
    .filter(c => selectedCategories.has(c.id));

  const Section = ({ title, children }) => (
    <div style={{ borderBottom: `1px solid ${T.n100}`, paddingBottom: 20, marginBottom: 20 }}>
      <p style={{ fontSize: 11, fontWeight: 600, color: T.n400, margin: "0 0 14px",
        textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: T.sans }}>{title}</p>
      {children}
    </div>
  );

  const Row = ({ label, value }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
      marginBottom: 8, flexWrap: "wrap", gap: 4 }}>
      <span style={{ fontSize: 13, color: T.n500, fontFamily: T.sans }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 500, color: T.n800, fontFamily: T.sans }}>{value}</span>
    </div>
  );

  return (
    <StepShell title="Review your application"
      subtitle="Check everything looks right before you submit.">

      <Section title="Personal information">
        <Row label="Full name" value={personal.fullName || "—"}/>
        <Row label="Phone" value={personal.phone || "—"}/>
        <Row label="Location" value={personal.location || "—"}/>
      </Section>

      <Section title="Services selected">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {cats.map(c => (
            <span key={c.id} style={{ padding: "5px 12px", borderRadius: 100,
              background: T.g50, color: T.g700, fontSize: 13,
              fontWeight: 500, fontFamily: T.sans,
              border: `1px solid ${T.g100}` }}>{c.name}</span>
          ))}
        </div>
      </Section>

      <Section title="Base rate">
        <Row label="Your rate" value={`KES ${rate.toLocaleString()}`}/>
      </Section>

      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: T.n400, margin: "0 0 14px",
          textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: T.sans }}>Availability</p>
        <Row label="Days" value={availability.availableDays.join(", ") || "—"}/>
        <Row label="Hours" value={`${availability.startHour} – ${availability.endHour}`}/>
      </div>

      {/* Terms */}
      <div style={{ background: T.amberL, border: `1px solid #fcd34d`,
        borderRadius: 12, padding: "14px 16px", display: "flex", gap: 10,
        alignItems: "flex-start", marginBottom: 8 }}>
        <Ico name="Info" size={15} color={T.amber} sw={1.5}/>
        <p style={{ fontSize: 12, color: T.amber, margin: 0, lineHeight: 1.65, fontFamily: T.sans }}>
          By submitting you agree to Jirani's Professional Terms of Service and confirm all information is accurate.
        </p>
      </div>

      <NavRow onBack={onBack} onNext={onSubmit} loading={loading}
        nextLabel="Submit application" canNext={true} step={7} totalSteps={6}/>
    </StepShell>
  );
}

/* ─── Shell wrapper for each form step ─── */
function StepShell({ title, subtitle, children }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ maxWidth: 520, width: "100%", margin: "0 auto",
      padding: isMobile ? "32px 20px 48px" : "48px 0 64px" }}>
      <h2 style={{ fontFamily: T.serif, fontSize: isMobile ? 28 : 34,
        fontWeight: 400, color: T.n900, margin: "0 0 10px",
        letterSpacing: "-0.03em", lineHeight: 1.15 }}>{title}</h2>
      <p style={{ fontSize: 15, color: T.n600, margin: "0 0 32px",
        lineHeight: 1.65, fontFamily: T.sans }}>{subtitle}</p>
      {children}
    </div>
  );
}

/* ─── Success screen ───
   NOTE: this is a purely client-side confirmation shown right after
   submit. It is NOT the same as a persisted notification/message —
   see the accompanying chat message for what's still missing on the
   backend to make this durable (visible again from /notifications
   or /messages after the user navigates away). */
function SuccessScreen({ onExit }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ maxWidth: 520, margin: "0 auto", textAlign: "center",
      padding: isMobile ? "60px 20px" : "80px 0", fontFamily: T.sans }}>

      <div style={{ width: 56, height: 56, borderRadius: "50%", background: T.g50,
        border: `1.5px solid ${T.g200}`, display: "flex", alignItems: "center",
        justifyContent: "center", margin: "0 auto 24px" }}>
        <Ico name="Check" size={24} color={T.g600} sw={2}/>
      </div>

      <h2 style={{ fontFamily: T.serif, fontSize: isMobile ? 28 : 34,
        fontWeight: 400, color: T.n900, margin: "0 0 12px",
        letterSpacing: "-0.03em" }}>Application received.</h2>

      <p style={{ fontSize: 16, color: T.n600, margin: "0 0 8px", lineHeight: 1.65 }}>
        Thank you for applying to become a JiraniPro.
      </p>
      <p style={{ fontSize: 14, color: T.n400, margin: "0 0 40px", lineHeight: 1.65 }}>
        Our team will review your profile and get back to you within{" "}
        <strong style={{ color: T.n700 }}>14 days</strong> by phone and email.
      </p>

      {/* What happens next */}
      <div style={{ background: T.n50, border: `1px solid ${T.n150}`,
        borderRadius: 16, padding: "24px 24px", marginBottom: 32, textAlign: "left" }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: T.n400, textTransform: "uppercase",
          letterSpacing: "0.08em", margin: "0 0 18px" }}>What happens next</p>
        {[
          { n: "1", t: "Application review", d: "Our team checks your profile, documents, and service listings." },
          { n: "2", t: "Background verification", d: "We verify your identity with our trusted partners." },
          { n: "3", t: "Decision notification", d: "You'll hear back within 14 days via SMS and email." },
        ].map((item, i) => (
          <div key={i} style={{ display: "flex", gap: 14, marginBottom: i < 2 ? 18 : 0 }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: T.g50,
              border: `1px solid ${T.g100}`, flexShrink: 0, display: "flex",
              alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.g600 }}>{item.n}</span>
            </div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600, color: T.n800, margin: "0 0 2px" }}>{item.t}</p>
              <p style={{ fontSize: 13, color: T.n500, margin: 0, lineHeight: 1.55 }}>{item.d}</p>
            </div>
          </div>
        ))}
      </div>

      <button onClick={onExit} style={{
        height: 52, padding: "0 40px", background: T.g500, color: T.white,
        border: "none", borderRadius: 12, fontSize: 15, fontWeight: 600,
        cursor: "pointer", fontFamily: T.sans,
        boxShadow: `0 4px 16px ${T.g500}44`, transition: "all .18s",
      }}
        onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
        onMouseLeave={e => e.currentTarget.style.opacity = "1"}
      >
        Return to home
      </button>

      <p style={{ fontSize: 12, color: T.n400, margin: "16px 0 0" }}>
        Questions? <a href="mailto:support@jiraniapp.com"
          style={{ color: T.g500, textDecoration: "none" }}>support@jiraniapp.com</a>
      </p>
    </div>
  );
}

/* ─── Progress bar (top of form steps) ─── */
function ProgressBar({ step, total, label }) {
  const pct = ((step) / total) * 100;
  return (
    <div style={{ maxWidth: 520, margin: "0 auto",
      padding: "24px 20px 0", width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "baseline", marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: T.n500, fontFamily: T.sans,
          textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Step {step} of {total}
        </span>
        <span style={{ fontSize: 13, color: T.n700, fontFamily: T.sans, fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ height: 3, background: T.n150, borderRadius: 99, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`,
          background: T.g500, borderRadius: 99, transition: "width .4s cubic-bezier(.4,0,.2,1)" }}/>
      </div>
    </div>
  );
}

/* ─── Top nav ───
   Runs full-width and edge-aligned instead of being capped inside a
   centered 1100px box. Padding matches the content edges on every
   step so the header never looks "condensed" relative to what's below it.
   Logo anchored far left, Exit/Save button anchored far right. */
function TopNav({ onExit, showProgress, step, totalSteps, stepLabel }) {
  const [scrolled, setScrolled] = useState(false);
  const isMobile = useIsMobile();
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <header style={{
      position: "sticky", top: 0, zIndex: 200,
      background: scrolled ? "rgba(255,255,255,.92)" : T.white,
      backdropFilter: scrolled ? "blur(8px)" : "none",
      borderBottom: `1px solid ${scrolled ? T.n150 : "transparent"}`,
      transition: "all .2s",
    }}>
      <div style={{ width: "100%", padding: isMobile ? "0 20px" : "0 56px",
        height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>

        <AppLogo />

        <button onClick={onExit} style={{
          background: "none", border: `1px solid ${T.n200}`,
          fontSize: 13, color: T.n500, fontFamily: T.sans,
          fontWeight: 500, cursor: "pointer",
          padding: "6px 16px", borderRadius: 8, transition: "all .15s",
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.n400; e.currentTarget.style.color = T.n800; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.n200; e.currentTarget.style.color = T.n500; }}
        >
          {showProgress ? "Save & exit" : "Exit"}
        </button>
      </div>
      {showProgress && (
        <ProgressBar step={step} total={totalSteps} label={stepLabel}/>
      )}
    </header>
  );
}

/* ─── Responsive hook ─── */
function useIsMobile() {
  const [m, setM] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return m;
}

/* ══════════════════════════════════════════════════
   MAIN EXPORT
══════════════════════════════════════════════════ */
export default function ProOnboarding() {
  const [step, setStep] = useState(0);   // 0 = welcome
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  /* Form state */
  const [personal, setPersonal] = useState({ fullName: "", phone: "", email: "", location: "" });
  const [selectedCats, setSelectedCats] = useState(new Set());
  const [rate, setRate] = useState(1000);
  const [identity, setIdentity] = useState({ idFront: null, idBack: null, selfie: null });
  const [availability, setAvailability] = useState({ availableDays: [], startHour: "08:00", endHour: "18:00" });

  const isMobile = useIsMobile();

  /* Load data */
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("categories").select("*").eq("is_active", true).order("sort_order");
      if (data?.length) setCategories(data);
    })();
    (async () => {
      // Email is still captured silently (not shown in the UI form anymore)
      // and submitted along with the profile below.
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) setPersonal(p => ({ ...p, email: user.email }));
    })();
  }, []);

  const toggleCat = id => setSelectedCats(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  const handleSubmit = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }

    // Resolved: AdminProReviewQueue was retired (Days 8-9 — it was dead
    // code, never functional). The real, live admin approval gate is
    // pro_profiles.account_status, wired up in AdminPros.jsx. New
    // applicants start "pending" so they appear in the admin moderation
    // queue and are correctly excluded from search/booking until approved.
    // user_id is set explicitly — it does not auto-populate, and every
    // real join elsewhere in the app (ProProfilePage, AdminPros) depends
    // on it pointing back to profiles.id.
    await supabase.from("pro_profiles").upsert({
      id: user.id, user_id: user.id, full_name: personal.fullName, phone: personal.phone,
      location: personal.location,
      account_status: "pending",
      updated_at: new Date().toISOString(),
    });

    const skills = Array.from(selectedCats).map(catId => ({
      pro_id: user.id, category_id: catId,
      base_rate: rate, is_approved: false, is_active: true,
    }));
    await supabase.from("pro_skills").insert(skills);
    // Childcare category requires a background-check request — created
    // automatically here if the pro selected any childcare-related service.
    // Uses upsert so resubmitting the application doesn't reset an
    // already-reviewed request back to "pending".
    const categoryList = categories.length ? categories : FALLBACK_CATEGORIES;
    const selectsChildcare = Array.from(selectedCats).some(catId => {
      const cat = categoryList.find(c => c.id === catId);
      return cat && /child|baby/i.test(cat.name);
    });
    if (selectsChildcare) {
      await supabase.from("background_check_requests").upsert(
        { pro_id: user.id, status: "pending" },
        { onConflict: "pro_id", ignoreDuplicates: true }
      );
    }

    // TODO: no row is currently written to a notifications/messages table here,
    // so the confirmation the user sees below is client-side only and won't
    // reappear in /notifications or /messages after they leave this page.
    // Once the schema for those tables is confirmed, add an insert here, e.g.:
    //   await supabase.from("notifications").insert({
    //     user_id: user.id, type: "application_submitted",
    //     title: "Application received",
    //     body: "We're reviewing your JiraniPro application.",
    //   });

    setLoading(false);
    setSubmitted(true);
  };

  /* Step metadata for progress bar */
  const FORM_STEPS = [
    { label: "About you",    n: 1 },
    { label: "Services",     n: 2 },
    { label: "Your rate",    n: 3 },
    { label: "Verify ID",    n: 4 },
    { label: "Availability", n: 5 },
    { label: "Review",       n: 6 },
  ];
  const formStep = step - 1; // 0-indexed within form
  const showProgress = step > 0 && !submitted;
  const stepMeta = FORM_STEPS[formStep] || { label: "", n: formStep + 1 };

  if (submitted) {
    return (
      <div style={{ background: T.white, minHeight: "100vh", fontFamily: T.sans }}>
        <TopNav onExit={() => window.location.href = "/"} showProgress={false}/>
        <SuccessScreen onExit={() => window.location.href = "/"}/>
        <GlobalStyles/>
      </div>
    );
  }

  return (
    <div style={{ background: T.white, minHeight: "100vh", fontFamily: T.sans }}>
      <TopNav onExit={() => window.location.href = "/"}
        showProgress={showProgress}
        step={stepMeta.n} totalSteps={6}
        stepLabel={stepMeta.label}/>

      <div style={{ maxWidth: step === 0 ? "none" : 560,
        margin: "0 auto", padding: step === 0 ? 0 : "0 20px" }}>

        {step === 0 && <StepWelcome onNext={() => setStep(1)}/>}
        {step === 1 && <StepPersonal data={personal} onChange={f => setPersonal(p => ({...p,...f}))}
          onNext={() => setStep(2)} onBack={() => setStep(0)}/>}
        {step === 2 && <StepServices categories={categories} selected={selectedCats}
          onToggle={toggleCat} onNext={() => setStep(3)} onBack={() => setStep(1)}/>}
        {step === 3 && <StepPricing rate={rate} onChange={setRate}
          onNext={() => setStep(4)} onBack={() => setStep(2)}/>}
        {step === 4 && <StepIdentity data={identity} onUpdate={f => setIdentity(p => ({...p,...f}))}
          onNext={() => setStep(5)} onBack={() => setStep(3)}/>}
        {step === 5 && <StepAvailability data={availability}
          onChange={f => setAvailability(p => ({...p,...f}))}
          onNext={() => setStep(6)} onBack={() => setStep(4)}/>}
        {step === 6 && <StepReview personal={personal} selectedCategories={selectedCats}
          categories={categories} rate={rate} availability={availability}
          onBack={() => setStep(5)} onSubmit={handleSubmit} loading={loading}/>}
      </div>

      <GlobalStyles/>
    </div>
  );
}

function GlobalStyles() {
  return (
    <style>{`
      @keyframes jrn-spin { to { transform: rotate(360deg); } }
      * { box-sizing: border-box; }
      body { margin: 0; background: #ffffff; }
      input[type=number]::-webkit-outer-spin-button,
      input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
      input[type=number] { -moz-appearance: textfield; }
      ::placeholder { color: #b0b7c8; }
      select { cursor: pointer; }
    `}</style>
  );
}